export const M4_UNIT_9 = {
  id: "m4-u9-simple-vs-continuous",
  title: "Unidad 9: Simple or Continuous?",
  topic: "Master the difference between present simple and present continuous! Learn when to use each tense with time markers and frequency adverbs. Essential for speaking naturally and avoiding common mistakes!",
  iconType: "clock",
  grammarImage: "/grammar/grammar-m4-u9.jpg",
  grammarText: "Present Simple = rutinas, hechos, hábitos (always, usually, every day). Present Continuous = acciones AHORA o temporales (now, right now, at the moment, today, this week). Estructura: Simple = I work/He works. Continuous = I am working/He is working.",
  
  sets: [
    // SET 1: Time Markers - Simple
    {
      id: "m4-u9-set1",
      title: "Set 1: Recognizing Routine Markers",
      questions: [
        {
          type: "match",
          q: "Match time marker to present simple:",
          pairs: [
            { left: "every day", right: "todos los días (routine)" },
            { left: "always", right: "siempre (100%)" },
            { left: "usually", right: "usualmente (80%)" }
          ],
          a: "completed",
          tip: "These words = present SIMPLE! They show habits and routines, not actions happening now."
        },
        {
          type: "mc",
          q: "'I ___ to work every day.' (Which form?)",
          options: ["go", "am going", "goes"],
          a: "go",
          tip: "'Every day' = routine = present simple! I/You/We/They + base verb."
        },
        {
          type: "input",
          q: "Complete: 'She always ___ coffee in the morning.' (drink)",
          a: "drinks"
        },
        {
          type: "order",
          q: "Ordena: 'Yo usualmente trabajo de lunes a viernes'",
          words: ["I", "usually", "work", "from", "Monday", "to", "Friday."],
          a: "I usually work from Monday to Friday."
        },
        {
          type: "mc",
          q: "Which verb form? 'He ___ the bus every morning.'",
          options: ["takes (verb+s)", "take (base verb)", "is taking (continuous)"],
          a: "takes (verb+s)",
          tip: "He/She/It + verb+S in present simple! 'Every morning' = routine marker."
        },
        {
          type: "input",
          q: "Complete: 'We ___ English classes twice a week.' (have)",
          a: "have"
        },
        {
          type: "mc",
          q: "'They never ___ late.' (Which form?)",
          options: ["arrive (base verb)", "arrives (verb+s)", "are arriving (continuous)"],
          a: "arrive (base verb)",
          tip: "'Never' = 0% frequency = present simple. They/We = base verb (no -s)!"
        },
        {
          type: "order",
          q: "Ordena: 'Ella siempre revisa su teléfono antes de dormir'",
          words: ["She", "always", "checks", "her", "phone", "before", "bed."],
          a: "She always checks her phone before bed.",
          successtip: "✓ BEFORE BED! Common routine! 📱 Phone addiction = universal. 'Before bed/sleep' = routine marker!"
        },
        {
          type: "input",
          q: "Complete: 'My boss sometimes ___ on weekends.' (work)",
          a: "works"
        },
        {
          type: "mc",
          q: "Identify: 'I often ___ my family on Sundays.'",
          options: ["visit (base verb - simple)", "am visiting (continuous)", "visits (verb+s)"],
          a: "visit (base verb - simple)",
          tip: "'Often' + 'on Sundays' = repeated habit = present simple!"
        }
      ]
    },

    // SET 2: Time Markers - Continuous
    {
      id: "m4-u9-set2",
      title: "Set 2: Recognizing 'Now' Markers",
      questions: [
        {
          type: "match",
          q: "Match time marker to present continuous:",
          pairs: [
            { left: "now", right: "ahora (este momento)" },
            { left: "right now", right: "ahora mismo (énfasis)" },
            { left: "at the moment", right: "en este momento" }
          ],
          a: "completed",
          tip: "These words = present CONTINUOUS! Actions happening at this exact moment!"
        },
        {
          type: "mc",
          q: "'I ___ my lunch right now.' (Which form?)",
          options: ["am eating (continuous)", "eat (simple)", "eats (verb+s)"],
          a: "am eating (continuous)",
          tip: "'Right now' = this exact moment = continuous! am/is/are + verb-ING"
        },
        {
          type: "input",
          q: "Complete: 'She ___ on the phone at the moment.' (talk)",
          a: "is talking"
        },
        {
          type: "order",
          q: "Ordena: 'Estoy trabajando en un proyecto ahora mismo'",
          words: ["I'm", "working", "on", "a", "project", "right", "now."],
          a: "I'm working on a project right now."
        },
        {
          type: "mc",
          q: "Which form? 'They ___ TV now.'",
          options: ["are watching (are + verb-ING)", "watch (base verb)", "watches (verb+s)"],
          a: "are watching (are + verb-ING)",
          tip: "'Now' = continuous marker! They/We + ARE + verb-ING."
        },
        {
          type: "input",
          q: "Complete: 'He ___ an email at the moment.' (write)",
          a: "is writing"
        },
        {
          type: "mc",
          q: "'Listen! Someone ___ at the door.' (Which tense?)",
          options: ["is knocking (continuous)", "knocks (simple)", "knock (base)"],
          a: "is knocking (continuous)",
          successtip: "✓ LISTEN! Command as marker! 👂 'Listen!' or 'Look!' = continuous! Drawing attention to NOW action!"
        },
        {
          type: "order",
          q: "Ordena: 'Ella está estudiando para su examen ahora'",
          words: ["She's", "studying", "for", "her", "exam", "now."],
          a: "She's studying for her exam now."
        },
        {
          type: "input",
          q: "Complete: 'We ___ for the bus right now.' (wait)",
          a: "are waiting"
        },
        {
          type: "mc",
          q: "Identify form: 'Be quiet! The baby ___.' (sleep - now)",
          options: ["is sleeping (is + verb-ING)", "sleeps (verb+s)", "sleep (base)"],
          a: "is sleeping (is + verb-ING)",
          tip: "'Be quiet!' = context shows NOW action = continuous!"
        }
      ]
    },

    // SET 3: Direct Comparison - Same Verb
    {
      id: "m4-u9-set3",
      title: "Set 3: Simple vs Continuous (Same Verb)",
      questions: [
        {
          type: "match",
          q: "Match context to correct tense:",
          pairs: [
            { left: "I work here (general)", right: "Present simple" },
            { left: "I'm working now (at this moment)", right: "Present continuous" },
            { left: "She studies daily (habit)", right: "Present simple" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "General fact: 'I ___ at Tim Hortons.' vs Now: 'I ___ right now.'",
          options: ["work, am working", "am working, work", "work, work"],
          a: "work, am working",
          tip: "Permanent job = simple. Action happening now = continuous. Same verb, different contexts!"
        },
        {
          type: "input",
          q: "Routine: 'She usually ___ at 7am.' vs Now: 'She ___ right now.' (wake up)",
          a: "wakes up, is waking up"
        },
        {
          type: "order",
          q: "Ordena: 'Usualmente cocino en casa, pero hoy estoy comiendo en un restaurante'",
          words: ["I", "usually", "cook", "at", "home,", "but", "today", "I'm", "eating", "at", "a", "restaurant."],
          a: "I usually cook at home, but today I'm eating at a restaurant.",
          successtip: "✓ CONTRAST! Key pattern! 🔑 'Usually' + simple VS 'today' + continuous. Shows habit vs exception!"
        },
        {
          type: "mc",
          q: "Choose correct: 'He always ___ the bus, but today he ___ his car.'",
          options: ["takes, is driving", "is taking, drives", "takes, drives"],
          a: "takes, is driving",
          tip: "'Always' = simple. 'Today' = continuous (temporary exception)."
        },
        {
          type: "input",
          q: "Fill both: 'We normally ___ English, but right now we ___ Spanish.' (speak)",
          a: "speak, are speaking"
        },
        {
          type: "mc",
          q: "ERROR: 'I am work at Walmart' - Identify problem:",
          options: [
            "Should be 'I work' (simple for permanent job)",
            "Missing -ing",
            "Wrong subject"
          ],
          a: "Should be 'I work' (simple for permanent job)",
          tip: "Permanent job = simple! 'I work at X' ✓. Don't use continuous for permanent situations!"
        },
        {
          type: "order",
          q: "Ordena: 'Normalmente trabajo de día pero esta semana estoy trabajando de noche'",
          words: ["I", "normally", "work", "days,", "but", "this", "week", "I'm", "working", "nights."],
          a: "I normally work days, but this week I'm working nights."
        },
        {
          type: "input",
          q: "Complete both: 'She ___ here (permanent), but she ___ from home today (temporary).' (work)",
          a: "works, is working"
        },
        {
          type: "mc",
          q: "Which is CORRECT? 'Where do you work?' - Response:",
          options: [
            "'I work at Shoppers Drug Mart' (simple)",
            "'I am working at Shoppers Drug Mart' (continuous)",
            "Both are correct"
          ],
          a: "'I work at Shoppers Drug Mart' (simple)",
          tip: "Permanent job = simple! Continuous only for temporary/in progress at THIS moment."
        }
      ]
    },

    // SET 4: Questions - Simple vs Continuous
    {
      id: "m4-u9-set4",
      title: "Set 4: Asking Questions",
      questions: [
        {
          type: "match",
          q: "Match question to tense:",
          pairs: [
            { left: "What do you do? (job)", right: "Present simple" },
            { left: "What are you doing? (now)", right: "Present continuous" },
            { left: "Where does he work? (general)", right: "Present simple" }
          ],
          a: "completed",
          successtip: "✓ KEY DIFFERENCE! Job vs action! 💼 'What do you do?' = profession. 'What are you doing?' = right now. Don't confuse!"
        },
        {
          type: "mc",
          q: "Asking about NOW: 'What ___ you ___ right now?'",
          options: ["are, doing", "do, do", "does, do"],
          a: "are, doing",
          tip: "NOW = continuous! ARE/IS + subject + verb-ING?"
        },
        {
          type: "input",
          q: "Asking about routine: 'Where ___ she work?' (does/is)",
          a: "does"
        },
        {
          type: "order",
          q: "Ordena: '¿Qué estás haciendo ahora?'",
          words: ["What", "are", "you", "doing", "now?"],
          a: "What are you doing now?"
        },
        {
          type: "mc",
          q: "Asking about habit: 'How often ___ you ___ to the gym?'",
          options: ["do, go", "are, going", "does, go"],
          a: "do, go",
          tip: "'How often' = frequency = present simple question! DO/DOES + subject + base verb?"
        },
        {
          type: "input",
          q: "Now question: '___ he working right now?' (Is/Does)",
          a: "Is"
        },
        {
          type: "mc",
          q: "General question: '___ they speak English at home?'",
          options: ["Do", "Are", "Is"],
          a: "Do",
          tip: "General habit = simple question! DO + they + base verb?"
        },
        {
          type: "order",
          q: "Ordena: '¿Dónde trabaja él?'",
          words: ["Where", "does", "he", "work?"],
          a: "Where does he work?"
        },
        {
          type: "input",
          q: "Complete: '___ you studying at the moment?' (Are/Do)",
          a: "Are"
        },
        {
          type: "mc",
          q: "ERROR: 'Where you are working?' - Correction:",
          options: [
            "'Where are you working?' (for temporary) OR 'Where do you work?' (for permanent)",
            "'Where you work?'",
            "'Where working you?'"
          ],
          a: "'Where are you working?' (for temporary) OR 'Where do you work?' (for permanent)",
          tip: "Question word + ARE + subject + -ING? OR Question word + DO/DOES + subject + base verb?"
        }
      ]
    },

    // SET 5: Temporary vs Permanent
    {
      id: "m4-u9-set5",
      title: "Set 5: Temporary Situations",
      questions: [
        {
          type: "match",
          q: "Match time expression to tense:",
          pairs: [
            { left: "this week/month/year", right: "Continuous (temporary period)" },
            { left: "every week/month/year", right: "Simple (routine)" },
            { left: "these days", right: "Continuous (current period)" }
          ],
          a: "completed",
          tip: "'This/these' = temporary period = continuous! 'Every' = routine = simple!"
        },
        {
          type: "mc",
          q: "'I ___ extra shifts this month to save money.' (temporary)",
          options: ["am working", "work", "works"],
          a: "am working",
          tip: "'This month' = temporary period = continuous, even if not RIGHT now!"
        },
        {
          type: "input",
          q: "Complete: 'She usually ___ in Toronto, but this year she ___ in Vancouver.' (live)",
          a: "lives, is living"
        },
        {
          type: "order",
          q: "Ordena: 'Estoy tomando clases de inglés este semestre'",
          words: ["I'm", "taking", "English", "classes", "this", "semester."],
          a: "I'm taking English classes this semester.",
          successtip: "✓ THIS SEMESTER! Temporary! 📚 Classes = temporary action (few months) = continuous. Not a permanent routine!"
        },
        {
          type: "mc",
          q: "'These days, I ___ from home because of the pandemic.'",
          options: ["am working", "work", "works"],
          a: "am working",
          tip: "'These days' = current temporary period = continuous!"
        },
        {
          type: "input",
          q: "Complete: 'He normally ___ by bus, but this week he ___ because the bus is on strike.' (walk, not take)",
          a: "takes, is walking"
        },
        {
          type: "mc",
          q: "'We ___ with my parents right now while we look for an apartment.' (stay temporarily)",
          options: ["are staying", "stay", "stays"],
          a: "are staying",
          successtip: "✓ STAYING! Temporary housing! 🏠 Many immigrants live with family first. 'Staying with' = temporary = continuous!"
        },
        {
          type: "order",
          q: "Ordena: 'Ella usualmente trabaja en oficina pero esta semana trabaja desde casa'",
          words: ["She", "usually", "works", "in", "the", "office,", "but", "this", "week", "she's", "working", "from", "home."],
          a: "She usually works in the office, but this week she's working from home."
        },
        {
          type: "input",
          q: "Complete: 'They ___ money for a house this year.' (save - temporary focus)",
          a: "are saving"
        },
        {
          type: "mc",
          q: "Which shows TEMPORARY situation? 'I ___ for my work permit.'",
          options: ["am waiting", "wait", "waits"],
          a: "am waiting",
          tip: "Waiting for permit = temporary ongoing situation = continuous! Not a routine!"
        }
      ]
    },

    // SET 6: Common Errors
    {
      id: "m4-u9-set6",
      title: "Set 6: Fixing Mistakes",
      questions: [
        {
          type: "match",
          q: "Match error to correction:",
          pairs: [
            { left: "I am work here", right: "I work here" },
            { left: "She work here", right: "She works here" },
            { left: "He working now", right: "He is working now" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "ERROR: 'I am go to work every day' - Correction:",
          options: [
            "'I go to work every day' (simple)",
            "'I going to work every day'",
            "'I am going to work every day'"
          ],
          a: "'I go to work every day' (simple)",
          tip: "'Every day' = routine = simple! Not continuous!"
        },
        {
          type: "input",
          q: "Correct: 'He work at Walmart' → 'He ___ at Walmart.'",
          a: "works"
        },
        {
          type: "order",
          q: "Corrige: 'She working right now' (add missing word)",
          words: ["She", "is", "working", "right", "now."],
          a: "She is working right now.",
          tip: "Continuous NEEDS am/is/are! 'She working' ❌ → 'She IS working' ✓"
        },
        {
          type: "mc",
          q: "ERROR: 'They are have two jobs' - Problem:",
          options: [
            "'Have' for possession = simple ('They have')",
            "Missing -ing",
            "Wrong subject"
          ],
          a: "'Have' for possession = simple ('They have')",
          tip: "'Have' = possession = state verb = simple! 'They have' ✓"
        },
        {
          type: "input",
          q: "Correct: 'I always am tired' → 'I ___ always tired.'",
          a: "am"
        },
        {
          type: "mc",
          q: "ERROR: 'She study English now' - Correction:",
          options: [
            "'She is studying English now' (continuous)",
            "'She studies English now'",
            "'She studying English now'"
          ],
          a: "'She is studying English now' (continuous)",
          tip: "'Now' = continuous! IS + verb-ING needed!"
        },
        {
          type: "order",
          q: "Corrige: 'I work right now at my computer'",
          words: ["I'm", "working", "at", "my", "computer", "right", "now."],
          a: "I'm working at my computer right now.",
          tip: "'Right now' = continuous! Change 'work' → 'am working'!"
        },
        {
          type: "input",
          q: "Correct: 'He don't works here' → 'He ___ work here.'",
          a: "doesn't"
        },
        {
          type: "mc",
          q: "ERROR: 'What you are doing?' - Correction:",
          options: [
            "'What are you doing?'",
            "'What you doing?'",
            "'What do you doing?'"
          ],
          a: "'What are you doing?'",
          successtip: "✓ WORD ORDER! Questions! ❓ Question word + ARE/IS + subject + -ING? NOT 'you are'!"
        }
      ]
    },

    // SET 7: Real Contexts - Work & Study
    {
      id: "m4-u9-set7",
      title: "Set 7: Work & Study Situations",
      questions: [
        {
          type: "mc",
          q: "'I usually ___ at 9am, but today I ___ at 10am because I have an appointment.'",
          options: ["start, am starting", "am starting, start", "start, start"],
          a: "start, am starting",
          tip: "'Usually' = simple. 'Today' = continuous (exception to routine)."
        },
        {
          type: "input",
          q: "Complete: 'She ___ at a hospital (permanent), but this month she ___ at a clinic (temporary).' (work)",
          a: "works, is working"
        },
        {
          type: "order",
          q: "Ordena: 'Estudio inglés todos los días, pero ahora mismo estoy estudiando matemáticas'",
          words: ["I", "study", "English", "every", "day,", "but", "right", "now", "I'm", "studying", "math."],
          a: "I study English every day, but right now I'm studying math."
        },
        {
          type: "mc",
          q: "'What ___ you ___?' - 'I'm a nurse.' (asking about profession)",
          options: ["do, do", "are, doing", "does, do"],
          a: "do, do",
          successtip: "✓ PROFESSION QUESTION! 'What do you do?' = job! 💼 Answer with simple: 'I'm a...' or 'I work as...'"
        },
        {
          type: "input",
          q: "Complete: 'He ___ overtime this week because they're short-staffed.' (work - temporary)",
          a: "is working"
        },
        {
          type: "mc",
          q: "'I can't talk now, I ___ in a meeting.'",
          options: ["am", "is", "do"],
          a: "am",
          tip: "'Right now' context + 'I am IN a meeting' = present continuous state!"
        },
        {
          type: "order",
          q: "Ordena: 'Normalmente termino a las 5, pero hoy estoy terminando a las 7'",
          words: ["I", "normally", "finish", "at", "5,", "but", "today", "I'm", "finishing", "at", "7."],
          a: "I normally finish at 5, but today I'm finishing at 7."
        },
        {
          type: "input",
          q: "Complete: 'Where ___ you work?' (simple - asking about job) vs 'Where ___ you working this week?' (temporary)",
          a: "do, are"
        },
        {
          type: "mc",
          q: "'She ___ for her citizenship exam these days.'",
          options: ["is studying", "studies", "study"],
          a: "is studying",
          successtip: "✓ CITIZENSHIP! Big goal! 🇨🇦 'These days' = temporary intense period = continuous. Exam prep = temporary focus!"
        },
        {
          type: "mc",
          q: "'I ___ two jobs (general), but right now I ___ only one because I quit the other.'",
          options: ["usually have, am working", "am usually having, work", "have usually, working"],
          a: "usually have, am working",
          tip: "'Usually have' = general. 'Right now am working' = current temporary situation."
        }
      ]
    },

    // SET 8: Real Contexts - Daily Life
    {
      id: "m4-u9-set8",
      title: "Set 8: Daily Life Situations",
      questions: [
        {
          type: "mc",
          q: "Phone call: 'Can you talk?' - 'Not really, I ___ dinner right now.'",
          options: ["am cooking", "cook", "cooks"],
          a: "am cooking",
          tip: "Phone = NOW context = continuous! 'I'm cooking' = happening at this moment!"
        },
        {
          type: "input",
          q: "Complete: 'I ___ the bus every day (routine), but today I ___ because the bus isn't running (now).' (take, walk)",
          a: "take, am walking"
        },
        {
          type: "order",
          q: "Ordena: 'Usualmente veo Netflix por la noche, pero esta noche estoy leyendo un libro'",
          words: ["I", "usually", "watch", "Netflix", "at", "night,", "but", "tonight", "I'm", "reading", "a", "book."],
          a: "I usually watch Netflix at night, but tonight I'm reading a book."
        },
        {
          type: "mc",
          q: "'Where ___ you live?' (permanent address) vs 'Where ___ you staying?' (temporary)",
          options: ["do, are", "are, do", "do, do"],
          a: "do, are",
          successtip: "✓ KEY DIFFERENCE! Permanent vs temporary! 🏠 'Where do you LIVE?' = home. 'Where are you STAYING?' = hotel/temporary!"
        },
        {
          type: "input",
          q: "Complete: 'My kids usually ___ at 8pm, but tonight they ___ late because it's Friday.' (sleep, stay up)",
          a: "sleep, are staying up"
        },
        {
          type: "mc",
          q: "'Listen! Someone ___ at the door!'",
          options: ["is knocking", "knocks", "knock"],
          a: "is knocking",
          tip: "'Listen!' = command pointing to NOW = continuous!"
        },
        {
          type: "order",
          q: "Ordena: 'Generalmente como en casa pero hoy estoy comiendo afuera'",
          words: ["I", "generally", "eat", "at", "home,", "but", "today", "I'm", "eating", "out."],
          a: "I generally eat at home, but today I'm eating out.",
          successtip: "✓ EATING OUT! 'Eat out' = comer en restaurante! 🍽️ Common phrasal verb. vs 'Eat in' = comer en casa!"
        },
        {
          type: "input",
          q: "Complete: 'She never ___ late (habit), but today she ___ because her alarm didn't ring (exception).' (arrive, arrive)",
          a: "arrives, is arriving"
        },
        {
          type: "mc",
          q: "'What ___ you ___ on weekends?' (routine) - 'I usually ___ with friends.'",
          options: ["do, do, hang out", "are, doing, am hanging out", "do, do, am hanging out"],
          a: "do, do, hang out",
          tip: "'On weekends' = routine = simple question + simple answer!"
        },
        {
          type: "mc",
          q: "'Be quiet! The kids ___ .' (sleep now)",
          options: ["are sleeping", "sleep", "sleeps"],
          a: "are sleeping"
        }
      ]
    },

    // SET 9: Advanced Integration
    {
      id: "m4-u9-set9",
      title: "Set 9: Complex Sentences",
      questions: [
        {
          type: "mc",
          q: "'I know you ___ busy, but we ___ to talk about this now.' (be busy - continuous, need - state)",
          options: ["are, need", "are being, are needing", "be, need"],
          a: "are, need",
          tip: "'Busy now' = continuous. 'Need' = state verb = simple. Mix them correctly!"
        },
        {
          type: "input",
          q: "Complete: 'She usually ___ happy, but today she ___ sad because of bad news.' (be, feel)",
          a: "is, is feeling / feels"
        },
        {
          type: "order",
          q: "Ordena: 'Sé que estás trabajando duro, pero necesitas descansar'",
          words: ["I", "know", "you're", "working", "hard,", "but", "you", "need", "to", "rest."],
          a: "I know you're working hard, but you need to rest.",
          successtip: "✓ MIXING TENSES! Natural English! 🎯 'I know' (state) + 'you're working' (now) + 'you need' (state). This IS fluency!"
        },
        {
          type: "mc",
          q: "'While I ___ for my work permit, I ___ English and ___ money.' (wait - temporary, learn - continuous, save - continuous)",
          options: ["am waiting, am learning, am saving", "wait, learn, save", "am waiting, learn, save"],
          a: "am waiting, am learning, am saving",
          tip: "All three = temporary ongoing situations = all continuous!"
        },
        {
          type: "input",
          q: "Complete: 'He ___ that life here is hard (opinion), but he ___ not to give up (continuous effort).' (think, try)",
          a: "thinks, is trying"
        },
        {
          type: "mc",
          q: "'I understand you ___ stressed, but everyone ___ difficult times right now.' (feel - now, go through - temporary period)",
          options: ["are feeling, is going through", "feel, goes through", "are feeling, go through"],
          a: "are feeling, is going through",
          tip: "'Understand' = state (simple). 'Feeling' = now (continuous). 'Going through' = temporary (continuous)."
        },
        {
          type: "order",
          q: "Ordena: 'Usualmente me siento bien, pero últimamente me siento estresado'",
          words: ["I", "usually", "feel", "good,", "but", "lately", "I'm", "feeling", "stressed."],
          a: "I usually feel good, but lately I'm feeling stressed."
        },
        {
          type: "input",
          q: "Complete: 'She ___ what she wants (know - state), and she ___ hard to achieve it (work - continuous effort).' (know, work)",
          a: "knows, is working"
        },
        {
          type: "mc",
          q: "'They normally ___ rent on time, but this month they ___ with payments because one of them lost their job.' (pay - habit, struggle - temporary)",
          options: ["pay, are struggling", "are paying, struggle", "pay, struggle"],
          a: "pay, are struggling",
          successtip: "✓ REAL IMMIGRANT STRUGGLE! 💸 Mixing tenses to express complex reality. This IS advanced English!"
        },
        {
          type: "mc",
          q: "'I believe things ___ better (opinion - state), and I ___ that my efforts ___ to pay off soon (think - believe, start - continuous change).'",
          options: ["are, believe, are starting", "be, am believing, start", "are, am believing, are starting"],
          a: "are, believe, are starting",
          tip: "'Are getting better' = continuous change. 'Believe' = state. 'Are starting' = beginning now."
        }
      ]
    },

    // SET 10: Real-Life Mastery
    {
      id: "m4-u9-set10",
      title: "Set 10: Real Conversations",
      questions: [
        {
          type: "mc",
          q: "A: 'What do you do?' B: 'I ___ as a cashier at Sobeys.' A: 'Oh, ___ you working today?' B: 'No, I ___ today off.'",
          options: [
            "work, are, have",
            "am working, do, am having",
            "work, do, am having"
          ],
          a: "work, are, have",
          tip: "'What do you do' = job (simple). 'Are you working' = today/now (continuous). 'Have' = possession (simple)."
        },
        {
          type: "input",
          q: "Complete: A: 'Where's Maria?' B: 'She ___ the bus to work right now.' (take - happening now)",
          a: "is taking"
        },
        {
          type: "order",
          q: "Ordena conversación: A: '¿Qué haces?' B: 'Soy enfermera.' A: '¿Dónde trabajas?' B: 'Trabajo en el Hospital General pero esta semana estoy trabajando en otra clínica.'",
          words: ["A:", "What", "do", "you", "do?", "B:", "I'm", "a", "nurse.", "A:", "Where", "do", "you", "work?", "B:", "I", "work", "at", "General", "Hospital,", "but", "this", "week", "I'm", "working", "at", "another", "clinic."],
          a: "A: What do you do? B: I'm a nurse. A: Where do you work? B: I work at General Hospital, but this week I'm working at another clinic.",
          successtip: "✓ PERFECT CONVERSATION! Natural flow! 💬 Job questions (simple) + exception (continuous). This IS how natives talk!"
        },
        {
          type: "mc",
          q: "Boss: 'Why ___ you late?' You: 'Sorry, the subway ___ delayed this morning.'",
          options: ["are, was", "do, is", "are, is"],
          a: "are, was",
          tip: "'Why are you late?' = NOW state. 'Was delayed' = past reason."
        },
        {
          type: "input",
          q: "Complete: Friend: 'Do you want to go out?' You: 'I can't, I ___ for my exam right now.' (study)",
          a: "am studying"
        },
        {
          type: "mc",
          q: "Family call: 'How ___ things going?' - 'Good! I ___ hard, and I ___ my new life here.'",
          options: ["are, am working, am enjoying", "do, work, enjoy", "are, work, am enjoying"],
          a: "are, am working, am enjoying",
          tip: "'How are things going' = continuous question. Both actions = temporary period = continuous!"
        },
        {
          type: "order",
          q: "Ordena: A: '¿Puedes hablar?' B: 'No, estoy manejando ahora. Te llamo después.'",
          words: ["A:", "Can", "you", "talk?", "B:", "No,", "I'm", "driving", "now.", "I'll", "call", "you", "later."],
          a: "A: Can you talk? B: No, I'm driving now. I'll call you later."
        },
        {
          type: "input",
          q: "Complete: Landlord: '___ you have any pets?' You: 'No, I ___ have any pets.' (Do, don't)",
          a: "Do, don't"
        },
        {
          type: "mc",
          q: "Job interview: 'Where ___ you see yourself in 5 years?' - Complex answer uses:",
          options: [
            "Both tenses (present simple for current + continuous for future plans)",
            "Only simple",
            "Only continuous"
          ],
          a: "Both tenses (present simple for current + continuous for future plans)",
          successtip: "✓ INTERVIEW GOLD! 'I currently work as X (simple), but I'm looking to grow (continuous) into Y role.' Mix tenses naturally!"
        },
        {
          type: "mc",
          q: "Final mastery: 'I ___ in Canada for three years now (general fact). I usually ___ by transit (routine), but today I ___ because it's nice out (now exception). I ___ English every day (habit), and I ___ better and better (continuous improvement). Right now I ___ my dream job (have permanently), but I also ___ for better opportunities (look - continuous). I ___ this is normal (believe - opinion), and I ___ to stay positive (try - continuous effort)!'",
          options: [
            "live, commute, am walking, study, am getting, have, am looking, believe, am trying",
            "am living, am commuting, walk, am studying, get, am having, look, am believing, try",
            "live, commute, walk, study, get, am having, am looking, am believing, am trying"
          ],
          a: "live, commute, am walking, study, am getting, have, am looking, believe, am trying",
          successtip: "✓ UNIT 9 MASTERED! Tense perfection! 🎯 You can now mix present simple and continuous naturally like a native speaker! Routines, now-actions, temporary situations, states - ALL CORRECT! This is advanced fluency! From 'I work' vs 'I'm working' confusion to natural mixing = HUGE achievement! 💪🔥🇨🇦"
        }
      ]
    }
  ],
// ===================================================================
// READING SET: Roberto's Life (Simple vs. Continuous)
// ===================================================================
readingSet: {
  id: "m4-u9-reading",
  title: "Reading: Roberto's Life in Toronto",
  category: "reading",

  questions: [
    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 1: Daily Routine & Right Now (preguntas 1, 3, 4, 5)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Routine vs. The Moment**

Hi, my name is Roberto and I want to tell you about my life right now. I live in Toronto and I work as a delivery driver for Uber Eats. I usually work from 5pm to midnight every day, including weekends. 

Right now, as I'm writing this, I'm sitting at a Tim Hortons waiting for orders. This is what I do between deliveries - I wait and I drink coffee! Today is busy, which is good. I'm making good money tonight.`
    },
    {
      type: "mc",
      q: "¿Por qué Roberto usa el Present Simple en la frase 'I work as a delivery driver'?",
      options: [
        "Es su trabajo permanente o situación actual general",
        "Está pasando exactamente en este micro-segundo",
        "Es un error gramatical"
      ],
      a: "Es su trabajo permanente o situación actual general",
      successtip: "✓ SIMPLE PRESENT!\nSe usa para hechos permanentes o situaciones estables. 💼"
    },
    {
      type: "mc",
      q: "Roberto dice: 'I usually work from 5pm to midnight'. ¿Por qué usa el Simple y no el Continuous?",
      options: [
        "Porque 'usually' indica frecuencia y rutina",
        "Debería haber usado el Continuous con -ing",
        "Porque es una acción que solo hará hoy"
      ],
      a: "Porque 'usually' indica frecuencia y rutina",
      successtip: "✓ ROUTINE!\nPalabras de frecuencia como *usually* o *every day* siempre piden Presente Simple. ⏰"
    },
    {
      type: "mc",
      q: "En la frase 'Right now I'm sitting at Tim Hortons', ¿por qué usa el Present Continuous?",
      options: [
        "Porque es una acción ocurriendo en este momento exacto",
        "Porque siempre vive sentado en Tim Hortons",
        "Es un error del texto"
      ],
      a: "Porque es una acción ocurriendo en este momento exacto",
      successtip: "✓ CONTINUOUS!\n*Right now* es la señal perfecta para usar el verbo con -ing. 🍩☕"
    },
    {
      type: "passage",
      passage: `**Current Circumstances**

Normally I commute by TTC, but these days I'm using my bike for deliveries because gas is expensive. 

Back in Mexico, I was an engineer, but here my degree doesn't count yet. So right now I'm working this job while I'm studying to get my Canadian certification. It takes time, but I'm getting there.`
    },
    {
      type: "mc",
      q: "Roberto dice: 'These days I'm using my bike'. ¿Cuál es la lógica aquí?",
      options: [
        "'These days' indica un periodo temporal actual, no permanente",
        "Debería ser simple porque lo hace todos los días",
        "Es una acción del pasado"
      ],
      a: "'These days' indica un periodo temporal actual, no permanente",
      successtip: "✓ TEMPORARY!\nEl Continuous no es solo para 'ahora mismo', también es para situaciones temporales de 'estos días'. 🚲"
    },
    {
      type: "mc",
      q: "'I'm working this job while I'm studying'. ¿Por qué ambos están en Continuous?",
      options: [
        "Porque ambas son situaciones temporales (no es su carrera final)",
        "Porque están pasando físicamente en este segundo",
        "Porque el Presente Simple está prohibido en esa frase"
      ],
      a: "Porque ambas son situaciones temporales (no es su carrera final)",
      successtip: "✓ LIFE IN PROGRESS!\nÉl ve este trabajo y sus estudios como un proceso en desarrollo, no algo estático. 📚🏗️"
    },
{
      type: "passage",
      passage: `**The Goal**

I'm also taking English classes three times a week. I'm improving every day! This month I'm working extra hours because I'm trying to save for my family's tickets. They're coming to visit in December and I'm SO excited! I miss them every single day. 

I know you're probably struggling right now, but remember - this is temporary. You're building something. Keep going!`
    },
    {
      type: "mc",
      q: "Roberto dice 'I miss them every single day'. ¿Por qué usa Simple y no Continuous?",
      options: [
        "'Miss' es un verbo de estado/emoción (State Verb) y no suele llevar -ing",
        "Debería ser 'I am missing'",
        "Es opcional usar cualquiera de los dos"
      ],
      a: "'Miss' es un verbo de estado/emoción (State Verb) y no suele llevar -ing",
      successtip: "✓ STATE VERB!\nSentimientos como *miss, love* o *hate* prefieren el Presente Simple. ❤️"
    },
    {
      type: "mc",
      q: "En conclusión, el texto mezcla ambos tiempos verbales para diferenciar:",
      options: [
        "Rutinas y hechos (Simple) vs. Acciones temporales o del momento (Continuous)",
        "Acciones buenas vs. acciones malas",
        "Pasado vs. Futuro"
      ],
      a: "Rutinas y hechos (Simple) vs. Acciones temporales o del momento (Continuous)",
      successtip: "✓ GRAMMAR MASTER!\nHas identificado la diferencia clave que da fluidez al inglés. 🏆"
    },
    {
      type: "mc",
      q: "Este texto es un ejemplo perfecto de:",
      options: [
        "Uso natural de los tiempos en un contexto real de inmigración",
        "Solo gramática básica de nivel 1",
        "Errores de un principiante que no sabe conjugar"
      ],
      a: "Uso natural de los tiempos en un contexto real de inmigración",
      successtip: "✓ AUTHENTIC MIXING! 🎉\n¡Felicidades! Has terminado el análisis. Esta es la voz real de un inmigrante usando inglés fluido. 🏆"
    }
  ]
},
  // REVIEW SET
  reviewSet: {
    id: "m4-u9-review",
    title: "Set 12: Unit Review",
    category: "review",
    questions: [
      {
        type: "match",
        q: "Match marker to tense:",
        pairs: [
          { left: "always, every day, usually", right: "Present simple" },
          { left: "now, right now, at the moment", right: "Present continuous" },
          { left: "this week/month, these days", right: "Present continuous" }
        ],
        a: "completed"
      },
      {
        type: "mc",
        q: "Present simple structure:",
        options: [
          "I/You/We/They + base verb | He/She/It + verb+s",
          "Always use -ING",
          "Always use AM/IS/ARE"
        ],
        a: "I/You/We/They + base verb | He/She/It + verb+s"
      },
      {
        type: "mc",
        q: "Present continuous structure:",
        options: [
          "AM/IS/ARE + verb-ING",
          "Just verb-ING",
          "DO/DOES + verb"
        ],
        a: "AM/IS/ARE + verb-ING"
      },
      {
        type: "input",
        q: "Routine: 'I ___ to work every day.' (go)",
        a: "go"
      },
      {
        type: "input",
        q: "Now: 'She ___ dinner right now.' (cook)",
        a: "is cooking"
      },
      {
        type: "order",
        q: "Ordena: 'Usualmente trabajo de día pero esta semana trabajo de noche'",
        words: ["I", "usually", "work", "days,", "but", "this", "week", "I'm", "working", "nights."],
        a: "I usually work days, but this week I'm working nights."
      },
      {
        type: "mc",
        q: "'What do you do?' asks about:",
        options: ["Your job (permanent)", "What you're doing now", "Where you live"],
        a: "Your job (permanent)"
      },
      {
        type: "mc",
        q: "'What are you doing?' asks about:",
        options: ["What you're doing right now", "Your job", "Your routine"],
        a: "What you're doing right now"
      },
      {
        type: "mc",
        q: "ERROR: 'I am work here' should be:",
        options: ["I work here", "I working here", "I am working here"],
        a: "I work here",
        tip: "Permanent job = simple! 'I work' ✓"
      },
      {
        type: "mc",
        q: "Final integration: 'I ___ in Toronto (permanent), I ___ at a restaurant (permanent job), but right now I ___ from home (temporary exception) because I ___ sick (now state). Usually I ___ happy with my job (general feeling), but lately I ___ stressed (temporary period). I ___ this is normal (opinion - state verb), and I ___ to stay positive (continuous effort).'",
        options: [
          "live, work, am working, am, feel, am feeling, think, am trying",
          "am living, am working, work, feel, feel, feel, am thinking, try",
          "live, work, work, am, am feeling, feel, am thinking, am trying"
        ],
        a: "live, work, am working, am, feel, am feeling, think, am trying",
        successtip: "✓ MODULE 4 COMPLETE! 🎉🎊 You've finished ALL 9 UNITS! From 'I am Maria' to natural tense mixing = INCREDIBLE JOURNEY! You can now: introduce yourself, describe routines, talk about feelings, express emotions with intensity, navigate transportation, and MIX PRESENT TENSES LIKE A NATIVE! This is real fluency! You're ready for real conversations! 💪🔥🇨🇦🎯"
      }
    ]
  }
};
